import { Connector, Pin, Wire } from '../types';
import { CONNECTOR_PINS } from './constants';

/** Height of one wire line and of a base (single-wire) pin row. */
export const WIRE_LINE_HEIGHT = CONNECTOR_PINS.ROW_HEIGHT;
/** Padding below the last row inside the connector body. */
const BODY_BOTTOM_PADDING = 10;
/** Smallest a connector is ever drawn, so a sparse connector still reads as a card. */
const MIN_TOTAL_HEIGHT = 90;

/** Per-pin wire counts keyed by pin id. */
export type PinWireCounts = Record<string, number>;

/** Placement of one pin row within the connector body table. */
export interface PinRowLayout {
  pinId: string;
  /** Row top, relative to the table origin (connector y + BODY_Y). */
  top: number;
  height: number;
  /** Row centre (where the pin dot and wire anchor sit), relative to the table origin. */
  centerY: number;
  wireCount: number;
}

export interface ConnectorLayout {
  rows: PinRowLayout[];
  /** Combined height of all pin rows. */
  pinsHeight: number;
}

/** Which wire line a point in the connector body resolves to. */
export interface WireRowHit {
  /** Pin the wire terminates on, or null when it sits in the unassigned block. */
  pinId: string | null;
  /** Zero-based line within the pin row or the unassigned block. */
  index: number;
}

/**
 * Resolves a vertical offset within the body table (already past the header) to
 * the wire line under it: a line within a pin row, or one in the trailing
 * unassigned block. Returns null above the table, on an empty pin row, or past
 * the last wire.
 */
export function wireRowAt(
  rows: PinRowLayout[],
  pinsHeight: number,
  unassignedCount: number,
  bodyY: number
): WireRowHit | null {
  if (bodyY < 0) {
    return null;
  }
  const row = rows.find(r => bodyY >= r.top && bodyY < r.top + r.height);
  if (row) {
    const index = Math.floor((bodyY - row.top) / WIRE_LINE_HEIGHT);
    if (index >= row.wireCount) {
      return null;
    }
    return { pinId: row.pinId, index };
  }
  const unassignedIndex = Math.floor((bodyY - pinsHeight) / WIRE_LINE_HEIGHT);
  if (unassignedIndex >= 0 && unassignedIndex < unassignedCount) {
    return { pinId: null, index: unassignedIndex };
  }
  return null;
}

/** Wire-ends terminating on each pin of a connector. */
export function pinWireCounts(connectorId: string, wires: Wire[]): PinWireCounts {
  const counts: PinWireCounts = {};
  wires.forEach(wire => {
    if (wire.startConnectorId === connectorId && wire.startPinId) {
      counts[wire.startPinId] = (counts[wire.startPinId] ?? 0) + 1;
    }
    if (wire.endConnectorId === connectorId && wire.endPinId) {
      counts[wire.endPinId] = (counts[wire.endPinId] ?? 0) + 1;
    }
  });
  return counts;
}

/** Height of a pin row given how many wires it carries (always at least one line). */
export function pinRowHeight(wireCount: number): number {
  return Math.max(1, wireCount) * WIRE_LINE_HEIGHT;
}

/**
 * Lays out the pin rows top to bottom, expanding rows that carry several wires.
 * With no counts every row is a single line, reproducing the compact layout.
 */
export function connectorLayout(pins: Pin[], counts: PinWireCounts = {}): ConnectorLayout {
  let top = 0;
  const rows = pins.map(pin => {
    const wireCount = counts[pin.id] ?? 0;
    const height = pinRowHeight(wireCount);
    const row: PinRowLayout = { pinId: pin.id, top, height, centerY: top + height / 2, wireCount };
    top += height;
    return row;
  });
  return { rows, pinsHeight: top };
}

/** Number of wires connected to a connector but not terminating on one of its pins. */
export function unassignedWireCount(connectorId: string, wires: Wire[]): number {
  return wires.filter(wire =>
    (wire.startConnectorId === connectorId && !wire.startPinId) ||
    (wire.endConnectorId === connectorId && !wire.endPinId)
  ).length;
}

/** Height of the body table: pin rows, unassigned wire lines and the trailing add-pin row. */
export function bodyTableHeight(pinsHeight: number, unassignedCount: number): number {
  return pinsHeight + unassignedCount * WIRE_LINE_HEIGHT + WIRE_LINE_HEIGHT;
}

/**
 * Full rendered height of a connector from its laid-out pin rows and unassigned
 * wire count. The single source of truth for connector height, used by both the
 * entity and the geometry helpers.
 */
export function totalHeight(pinsHeight: number, unassignedCount: number): number {
  return Math.max(MIN_TOTAL_HEIGHT, CONNECTOR_PINS.BODY_Y + bodyTableHeight(pinsHeight, unassignedCount) + BODY_BOTTOM_PADDING);
}

/** Full rendered height of a connector, grown to fit its pins and connected wires. */
export function connectorHeight(connector: Connector, wires: Wire[]): number {
  const counts = pinWireCounts(connector.id, wires);
  const { pinsHeight } = connectorLayout(connector.pins ?? [], counts);
  return totalHeight(pinsHeight, unassignedWireCount(connector.id, wires));
}
